import { useEffect, useMemo, useState } from "react";
import { Activity, Braces, ExternalLink, Palette, RotateCcw, Save } from "lucide-react";
import { formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, InlineAlert, Input, NativeSelect, Skeleton, StatCard, ToggleGroup, ToggleGroupItem, notifySaved, saveFailureText, toast } from "@ncp/ui";
import { CodeEditor, jsonError } from "../../../components/CodeEditor";
import { usePages, useSaveSpaceManifest, useSpaceManifest, useSpaceUsage, type SpaceManifest } from "../../pages/api";
import type { BuilderRef, SpaceBuiltins } from "../api";
import { SectionTitle } from "./controls";
import { GrantsSection, HomeSection, MatterTabsSection, NavSection, PagesListSection, UseCasesSection } from "./sections";

const THEMES = ["ncp", "seablue", "camel", "aegreen", "slate"];
const VOCABULARIES = ["studies"];

const normalise = (m: Partial<SpaceManifest>): SpaceManifest => ({
  version: m.version ?? 1, source: m.source, theme: m.theme ?? null, vocabulary: m.vocabulary ?? null,
  home: m.home ?? [], nav: m.nav ?? [], matterTabs: m.matterTabs ?? {}, workbench: m.workbench ?? [], useCases: m.useCases ?? [], grants: m.grants ?? {}, mounts: m.mounts ?? [],
});
const same = (a: SpaceManifest, b: SpaceManifest) => JSON.stringify(normalise(a)) === JSON.stringify(normalise(b));

/**
 * The space designer: what a space is made of in the AI Workspace – its home, navigation, matter tabs grouped by stage, workbench pages,
 * use cases, the grants its pages hold, pages mounted from other spaces, theme and vocabulary – with how the space is used.
 */
export function SpaceDesigner({ spaceKey, canWrite, playbooks, builtins, onSaved, onOpen }: {
  spaceKey: string;
  canWrite: boolean;
  /** The playbooks of this workspace (matter tabs are set per playbook). */
  playbooks: BuilderRef[];
  builtins: SpaceBuiltins | undefined;
  onSaved: (manifest: SpaceManifest) => void;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const loaded = useSpaceManifest(spaceKey);
  const pagesQuery = usePages(false);
  const save = useSaveSpaceManifest(spaceKey);
  const [m, setM] = useState<SpaceManifest | null>(null);
  const [base, setBase] = useState<SpaceManifest | null>(null);
  const [mode, setMode] = useState<"visual" | "json">("visual");
  const [jsonText, setJsonText] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!loaded.data || (m && base && !same(m, base))) return;
    const next = normalise(loaded.data.manifest);
    setM(next); setBase(next);
  }, [loaded.data]);

  const pages = useMemo(() => (pagesQuery.isError ? null : (pagesQuery.data ?? []).filter((p) => p.visibility !== "Personal" && p.origin !== "suggestion")), [pagesQuery.data, pagesQuery.isError]);
  const readOnly = !canWrite;
  if (loaded.isLoading || !m || !base) return <Skeleton className="h-96" />;
  const dirty = !same(m, base);
  const pack = base.source?.startsWith("repo:") ? base.source.split(":")[1] ?? null : null;
  const set = (patch: Partial<SpaceManifest>) => { if (!readOnly) { setM((x) => ({ ...x!, ...patch })); setSaveError(null); } };
  const jsonProblem = mode === "json" ? jsonError(jsonText) : null;

  const persist = async () => {
    try {
      const answer = await save.mutateAsync(m);
      const saved = normalise(answer);
      setM(saved); setBase(saved); setSaveError(null);
      onSaved(saved);
      notifySaved(answer, t("admin:spaceDesigner.saved"));
    } catch (e) { setSaveError(saveFailureText(e) || t("admin:common.error")); /* useSave reported it as a toast too */ }
  };

  const toJson = () => { setJsonText(JSON.stringify(m, null, 2)); setMode("json"); };
  const fromJson = () => {
    if (jsonProblem) return;
    try { setM(normalise(JSON.parse(jsonText) as SpaceManifest)); setMode("visual"); } catch (e) { toast.error((e as Error).message); }
  };

  return (
    <div className="space-y-4">
      <div className="sticky top-[7.5rem] z-10 flex flex-wrap items-center gap-2 rounded-xl border border-sand-200 bg-white/95 px-3 py-2 backdrop-blur">
        <span className="text-sm font-medium">{t("admin:spaceDesigner.title")}</span>
        {pack ? <Badge size="sm" variant="camel">{t("admin:spaceDesigner.source.repo", { folder: pack })}</Badge> : base.source === "generated" ? <Badge size="sm" variant="camel">{t("admin:spaceDesigner.source.generated")}</Badge> : null}
        {dirty ? <Badge size="sm" variant="warning">{t("admin:common.unsaved")}</Badge> : null}
        <ToggleGroup type="single" size="sm" value={mode} onValueChange={(v) => (v === "json" ? toJson() : v === "visual" ? fromJson() : undefined)} className="ms-auto">
          <ToggleGroupItem value="visual">{t("admin:common.visual")}</ToggleGroupItem>
          <ToggleGroupItem value="json"><Braces className="size-3.5" /> {t("admin:common.json")}</ToggleGroupItem>
        </ToggleGroup>
        <Button size="sm" variant="ghost" onClick={onOpen}><ExternalLink /> {t("admin:spaceDesigner.open")}</Button>
        {canWrite ? (<>
          <Button size="sm" variant="ghost" disabled={!dirty} onClick={() => { setM(base); setSaveError(null); }}><RotateCcw /> {t("admin:spaceDesigner.reset")}</Button>
          <Button size="sm" variant={dirty ? "gold" : "outline"} disabled={!dirty || mode === "json"} loading={save.isPending} onClick={() => void persist()}><Save /> {t("admin:spaceDesigner.save")}</Button>
        </>) : null}
      </div>

      <InlineAlert tone="info">{t("admin:spaceDesigner.intro")}</InlineAlert>
      {pack && canWrite ? <InlineAlert tone="warning">{t("admin:spaceDesigner.packNote", { folder: pack })}</InlineAlert> : null}
      {saveError ? <InlineAlert tone="danger" title={t("admin:spaceDesigner.notSaved")}>{saveError}</InlineAlert> : null}
      {!saveError && loaded.data?.issues.length && !dirty ? (
        <InlineAlert tone="warning" title={t("admin:spaceDesigner.issues")}><ul className="list-disc ps-4">{loaded.data.issues.map((i) => <li key={i}>{i}</li>)}</ul></InlineAlert>
      ) : null}
      {pagesQuery.isError ? <InlineAlert tone="info">{t("admin:spaceDesigner.noPageList")}</InlineAlert> : null}

      {mode === "json" ? (
        <div className="space-y-2">
          <CodeEditor value={jsonText} onChange={setJsonText} language="json" readOnly={readOnly} height="65vh" />
          {jsonProblem ? <p className="text-xs text-danger-700">{jsonProblem}</p> : <p className="text-xs text-muted-foreground">{t("admin:spaceDesigner.jsonHint")}</p>}
        </div>
      ) : (
        <>
          <HomeSection m={m} set={set} pages={pages} readOnly={readOnly} builtins={builtins} />
          <NavSection m={m} set={set} pages={pages} readOnly={readOnly} builtins={builtins} />
          <UseCasesSection m={m} set={set} pages={pages} readOnly={readOnly} playbooks={playbooks} />
          <MatterTabsSection m={m} set={set} pages={pages} readOnly={readOnly} playbooks={playbooks} builtins={builtins} />
          <div className="grid gap-4 xl:grid-cols-2">
            <PagesListSection m={m} set={set} pages={pages} readOnly={readOnly} field="workbench" />
            <PagesListSection m={m} set={set} pages={pages} readOnly={readOnly} field="mounts" />
          </div>
          <GrantsSection m={m} set={set} pages={pages} readOnly={readOnly} />
          <Card><CardContent className="pt-5">
            <SectionTitle icon={<Palette className="size-4 text-aegold-700" />} title={t("admin:spaceDesigner.look.title")} hint={t("admin:spaceDesigner.look.hint")} />
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="space-y-1 text-xs"><span className="font-medium">{t("admin:spaceDesigner.look.theme")}</span>
                <NativeSelect value={m.theme ?? ""} disabled={readOnly} onChange={(e) => set({ theme: e.target.value || null })}>
                  <option value="">{t("admin:spaceDesigner.look.defaultTheme")}</option>
                  {THEMES.map((th) => <option key={th} value={th}>{t(`admin:spaceDesigner.look.themes.${th}`, { defaultValue: th })}</option>)}
                </NativeSelect>
              </label>
              <label className="space-y-1 text-xs"><span className="font-medium">{t("admin:spaceDesigner.look.vocabulary")}</span>
                <Input list="space-vocab" value={m.vocabulary ?? ""} readOnly={readOnly} dir="ltr" className="font-mono" placeholder={t("admin:spaceDesigner.look.standardWords")} onChange={(e) => set({ vocabulary: e.target.value.trim() || null })} />
                <datalist id="space-vocab">{VOCABULARIES.map((v) => <option key={v} value={v} />)}</datalist>
              </label>
            </div>
          </CardContent></Card>
          <SpaceUsageCard spaceKey={spaceKey} />
        </>
      )}
    </div>
  );
}

function SpaceUsageCard({ spaceKey }: { spaceKey: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [days, setDays] = useState(30);
  const usage = useSpaceUsage(spaceKey, days);
  const u = usage.data;
  return (
    <Card><CardContent className="pt-5">
      <SectionTitle icon={<Activity className="size-4 text-aegold-700" />} title={t("admin:spaceDesigner.usage.title")} hint={t("admin:spaceDesigner.usage.hint")}
        actions={<NativeSelect value={String(days)} onChange={(e) => setDays(Number(e.target.value))} className="h-8 w-36">{[7, 30, 90].map((d) => <option key={d} value={d}>{t("admin:pageStudio.usage.days", { count: d })}</option>)}</NativeSelect>} />
      {usage.isLoading ? <Skeleton className="h-32" /> : !u ? null : (
        <>
          <div className="mb-3 grid gap-3 sm:grid-cols-3">
            <StatCard label={t("admin:pageStudio.usage.people")} value={u.people} tone="sea" />
            <StatCard label={t("admin:pageStudio.usage.opens")} value={u.opens} tone="gold" />
            <StatCard label={t("admin:spaceDesigner.usage.pages")} value={u.pages.length} tone="camel" />
          </div>
          {u.pages.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:pageStudio.usage.none")}</p> : (
            <div className="overflow-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-sand-200 text-muted-foreground">
                  <th className="py-1 text-start font-medium">{t("admin:spaceDesigner.usage.page")}</th>
                  {(["opens", "people", "turns", "calls", "errors", "refused"] as const).map((k) => <th key={k} className="text-end font-medium">{t(`admin:spaceDesigner.usage.${k}`)}</th>)}
                  <th className="text-end font-medium">{t("admin:spaceDesigner.usage.suggestions")}</th>
                </tr></thead>
                <tbody>{u.pages.map((p) => (
                  <tr key={p.key} className="border-b border-sand-100">
                    <td className="py-1.5"><span className="font-medium">{p.name ?? p.key}</span> <code className="text-muted-foreground" dir="ltr">{p.key}</code></td>
                    {[p.opens, p.people, p.agentTurns, p.calls, p.errors, p.refused].map((n, i) => <td key={i} className="text-end tabular">{formatNumber(n, locale)}</td>)}
                    <td className="text-end">{Object.entries(p.suggestions).map(([s, n]) => <Badge key={s} size="sm" variant="outline" className="ms-1">{t(`admin:pageStudio.suggestions.status.${s}`, { defaultValue: s })} {n}</Badge>)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </>
      )}
    </CardContent></Card>
  );
}
